import React from 'react';
import {
  CoffeeOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { ScheduleException } from '../../../services/scheduleApi';

export interface ExceptionBlockProps {
  exception: ScheduleException;
  /** Pixel offset within the lane track (from lane math). */
  top: number;
  /** Pixel height within the lane track (from lane math). */
  height: number;
  /** Fires on tap — opens the BlockTimeDrawer in edit mode. */
  onEdit: () => void;
}

// Deterministic fallback swatches when the owner has no calendar_color set.
// Picked by user_id so the same tech always gets the same tint. Muted tones
// so a block never competes with a real appointment for attention.
const FALLBACK_PALETTE = ['#8c8c8c', '#5b8ff9', '#5ad8a6', '#5d7092', '#f6bd16', '#e8684a'];

function ownerColor(ex: ScheduleException): string {
  if (ex.calendar_color) return ex.calendar_color;
  const idx = Math.abs(Number(ex.user_id) || 0) % FALLBACK_PALETTE.length;
  return FALLBACK_PALETTE[idx];
}

// Short display label + icon per exception category. Two staff categories both
// persist as 'break' server-side, so 'break' reads as the generic "Break".
function categoryMeta(type: ScheduleException['exception_type']): { label: string; icon: React.ReactNode } {
  switch (type) {
    case 'break':
      return { label: 'Break', icon: <CoffeeOutlined /> };
    case 'time_off':
      return { label: 'Time off', icon: <StopOutlined /> };
    case 'special_hours':
      return { label: 'Hours', icon: <ClockCircleOutlined /> };
    case 'holiday':
      return { label: 'Holiday', icon: <CalendarOutlined /> };
    default:
      return { label: 'Blocked', icon: <StopOutlined /> };
  }
}

/**
 * A muted, non-appointment availability band drawn on the Week/Day lane grids.
 * Visually distinct from ApptBlock — a low-opacity fill overlaid with a
 * diagonal hatch and a solid left edge in the owner's color — so it reads as
 * "unavailable", never as a bookable job. Absolutely positioned by the
 * top/height the caller derives from the shared lane math.
 */
export function ExceptionBlock({ exception, top, height, onEdit }: ExceptionBlockProps) {
  const color = ownerColor(exception);
  const { label, icon } = categoryMeta(exception.exception_type);
  const reason = exception.reason;
  const tooltip = [label, reason, exception.group_id ? 'Shop-wide' : '']
    .filter(Boolean)
    .join(' · ');

  // Diagonal hatch layered over a faint fill — the hatch is what keeps a block
  // from ever reading as a solid colored appointment card.
  const hatch = `repeating-linear-gradient(45deg, ${color}22 0, ${color}22 6px, transparent 6px, transparent 12px)`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onEdit();
      }}
      data-testid="exception-block"
      data-exception-id={exception.id}
      title={tooltip}
      style={{
        position: 'absolute',
        top,
        left: 2,
        right: 2,
        height,
        zIndex: 0,
        boxSizing: 'border-box',
        borderLeft: `3px solid ${color}`,
        borderRadius: 4,
        background: `${hatch}, ${color}14`,
        color: 'rgba(0,0,0,0.55)',
        padding: '1px 6px',
        overflow: 'hidden',
        fontSize: 11,
        lineHeight: 1.25,
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <span aria-hidden>{icon}</span>
        <span>{label}</span>
      </div>
      {reason && (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reason}</div>
      )}
    </div>
  );
}

export default ExceptionBlock;
